import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import CrmTeam from "@/components/CrmTeam";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, PencilSimple, ShieldCheck, Tray, ArrowClockwise, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";

export const STATUSES = [
  { v: "new", l: "New" },
  { v: "contacted", l: "Contacted" },
  { v: "in_conversation", l: "In conversation" },
  { v: "proposal", l: "Proposal" },
  { v: "won", l: "Won" },
  { v: "lost", l: "Lost" },
];
const STATUS_STYLE = {
  new: "bg-[#EAF0F6] text-[#17324D]",
  contacted: "bg-sky-50 text-sky-800",
  in_conversation: "bg-indigo-50 text-indigo-800",
  proposal: "bg-amber-50 text-amber-800",
  won: "bg-emerald-50 text-emerald-800",
  lost: "bg-stone-100 text-stone-500",
};
const CATEGORIES = ["Food Startups", "Established Food Companies", "Strategic Partners"];
const OPPS = ["", "<$2,500", "$2,500–5,000", "$5,000–10,000", "$10,000–25,000", "$25,000+"];
// Mirrors LikelyNeed Literal in alaines-bench-api/crm.py exactly.
const LIKELY_NEEDS = [
  "", "New product formulation", "Seasoning development", "Flavor optimization",
  "Reformulation", "Cost reduction", "Sodium reduction", "Ingredient substitution",
  "Supplier transition", "Commercialization", "Scale-up support",
  "Co-manufacturer preparation", "Specification development", "Labeling documentation",
  "Sensory evaluation", "Manufacturing troubleshooting", "Shelf-life strategy",
  "Product line extension", "Clean label reformulation", "Flavor matching",
  "Competitive benchmarking", "Sensory optimization", "Pilot plant support",
  "Plant trial support", "Process optimization", "Cost-in-use optimization",
  "Vendor qualification", "Shelf-life validation", "Technical documentation",
  "Innovation strategy", "Alaine's Bench subscription", "Alaine's Compass subscription",
];
// Mirrors ProspectUpdate max_length limits in crm.py.
const MAX = { name: 160, contactName: 160, contactEmail: 254, notes: 40000, whyFit: 1000, assignedTo: 160, scope: 10000 };
const EMPTY = { name: "", category: "Food Startups", status: "new", contactName: "", contactEmail: "", whyFit: "", likelyNeed: "", notes: "", estOpportunity: "", nextFollowup: "", lastContact: "", assignedTo: "", deadline: "", scope: "" };

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const statusLabel = (s) => STATUSES.find((x) => x.v === s)?.l || s || "—";
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d.length === 10 ? d + "T00:00:00" : d);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const isOpen = (p) => p.status !== "won" && p.status !== "lost";
const followState = (p) => {
  if (!p.nextFollowup || !isOpen(p)) return null;
  const d = p.nextFollowup.slice(0, 10), t = today();
  if (d < t) return "overdue";
  if (d === t) return "due";
  return null;
};
const sel = "h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#17324D]/30";

export default function ClientsPipeline() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("prospects");
  const [prospects, setProspects] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fCat, setFCat] = useState("");
  const [editing, setEditing] = useState(null); // null | {id?, ...form}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, i] = await Promise.all([
        api.get("/crm/prospects", { skipNetToast: true }),
        api.get("/crm/inquiries", { skipNetToast: true }),
      ]);
      if (!Array.isArray(p.data?.prospects)) throw new Error("Unexpected response from /crm/prospects.");
      if (!Array.isArray(i.data?.inquiries)) throw new Error("Unexpected response from /crm/inquiries.");
      setProspects(p.data.prospects);
      setInquiries(i.data.inquiries);
    } catch (e) {
      setError(e?.response?.status === 403 ? "Administrator access required." : e?.response ? formatApiError(e.response.data?.detail) : (e?.message || "Could not load pipeline."));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return prospects.filter((p) => {
      if (fStatus && p.status !== fStatus) return false;
      if (fCat && p.category !== fCat) return false;
      if (tab === "won" && p.status !== "won") return false;
      if (tab === "followups" && !followState(p)) return false;
      if (!s) return true;
      return [p.name, p.contactName, p.contactEmail, p.notes, p.whyFit, p.assignedTo, p.scope].some((v) => (v || "").toLowerCase().includes(s));
    }).sort((a, b) => {
      if (tab === "followups") return (a.nextFollowup || "").localeCompare(b.nextFollowup || "");
      const clientFirst = Number(b.status === "won") - Number(a.status === "won");
      return clientFirst || (b.updatedAt || "").localeCompare(a.updatedAt || "");
    });
  }, [prospects, q, fStatus, fCat, tab]);

  const dueCount = prospects.filter(followState).length;
  const openCount = prospects.filter(isOpen).length;
  const wonCount = prospects.filter((p) => p.status === "won").length;

  const openEdit = (p) => {
    if (!p) { setEditing({ ...EMPTY }); return; }
    const f = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, /nextFollowup|lastContact|deadline/.test(k) ? (p[k] || "").slice(0, 10) : (p[k] ?? "")]));
    setEditing({ ...f, status: f.status || "new", category: f.category || "Food Startups", id: p.id });
  };
  const openById = (id) => { const p = prospects.find((x) => x.id === id); if (p) openEdit(p); else toast.error("Prospect not found — try reloading."); };

  const save = async (e) => {
    e.preventDefault();
    if (!editing.name.trim()) { toast.error("Company name is required."); return; }
    if (editing.likelyNeed && !LIKELY_NEEDS.includes(editing.likelyNeed)) { toast.error("Choose a likely need from the list."); return; }
    if (editing.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editing.contactEmail.trim())) { toast.error("Contact email looks invalid."); return; }
    setSaving(true);
    const { id, ...body } = editing;
    Object.keys(body).forEach((k) => { if (typeof body[k] === "string") body[k] = body[k].trim(); });
    try {
      const { data } = id ? await api.patch(`/crm/prospects/${id}`, body) : await api.post("/crm/prospects", body);
      const saved = data?.prospect;
      if (!saved || typeof saved !== "object" || !saved.id) {
        toast.error("Save response was malformed. Reload to confirm whether the change was stored.");
        return;
      }
      setProspects((list) => id ? list.map((x) => x.id === id ? saved : x) : [saved, ...list]);
      toast.success(id ? "Prospect updated" : "Prospect added");
      setEditing(null);
    } catch (err) {
      if (!err.handledToast) toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#C69A4B] font-semibold">Internal · Alaine &amp; Co.</div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#17324D]">Clients &amp; Pipeline</h1>
            <p className="text-sm text-[#5B6570] mt-1">{wonCount} won clients · {openCount} open · {prospects.length} total · {dueCount} follow-up{dueCount === 1 ? "" : "s"} due</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5" data-testid="button-reload-crm"><ArrowClockwise size={15} /> Reload</Button>
            <Button size="sm" onClick={() => openEdit(null)} className="gap-1.5 bg-[#17324D] hover:bg-[#22476B]" data-testid="button-new-prospect"><Plus size={15} weight="bold" /> New prospect</Button>
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-[#C5D3E2] bg-[#EAF0F6] px-3 py-2 text-xs text-[#17324D] mb-5" data-testid="crm-admin-notice">
          <ShieldCheck size={16} weight="fill" className="shrink-0 mt-px" />
          <span><b>Private team CRM.</b> Authorized employees can view and update all CRM records. “Assigned to” tracks responsibility, not access. Formulas remain private. No emails are sent from this page.</span>
        </div>

        {isAdmin && <details className="mb-5"><summary className="cursor-pointer text-sm font-semibold text-[#17324D]">Manage employee access</summary><div className="mt-3"><CrmTeam /></div></details>}

        <div role="tablist" aria-label="Pipeline views" className="flex flex-wrap gap-1 border-b border-stone-200 mb-4">
          {[["prospects", "All records"], ["won", `Clients / Won (${wonCount})`], ["followups", `Due / overdue (${dueCount})`], ["inquiries", `Website inquiries (${inquiries.length})`]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => { setTab(k); setQ(""); setFStatus(""); setFCat(""); }} data-testid={`tab-${k}`}
              className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k ? "border-[#C69A4B] text-[#17324D] font-semibold" : "border-transparent text-[#5B6570] hover:text-[#17324D]"}`}>{l}</button>
          ))}
        </div>

        {tab !== "inquiries" && (
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_220px] gap-2 mb-4">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input aria-label="Search prospects" placeholder="Search company, contact, notes…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 bg-white" data-testid="input-search-prospects" />
            </div>
            <select aria-label="Filter by stage" className={sel} value={tab === "won" ? "won" : fStatus} disabled={tab === "won"} onChange={(e) => setFStatus(e.target.value)} data-testid="select-filter-status">
              <option value="">All stages</option>{STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <select aria-label="Filter by category" className={sel} value={fCat} onChange={(e) => setFCat(e.target.value)} data-testid="select-filter-category">
              <option value="">All categories</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        )}

        {loading ? (
          <div className="space-y-2" aria-busy="true">{[0, 1, 2, 3].map((i) => <div key={i} className="h-16 rounded-md bg-stone-200/60 animate-pulse" />)}</div>
        ) : error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-6 text-center" role="alert">
            <p className="text-sm text-red-800 mb-3">{error}</p>
            <Button size="sm" variant="outline" onClick={load}>Try again</Button>
          </div>
        ) : tab === "inquiries" ? (
          inquiries.length === 0 ? <Empty title="No website inquiries yet" body="Inquiries submitted on the Alaine & Co. site appear here automatically." /> : (
            <ul className="space-y-2" data-testid="list-inquiries">
              {[...inquiries].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")).map((i) => (
                <li key={i.id} className="rounded-md border border-stone-200 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#17324D]">{i.company || i.name}</div>
                      <div className="text-xs text-[#5B6570]">{i.name}{i.email ? ` · ${i.email}` : ""}{i.service ? ` · ${i.service}` : ""} · {fmtDate(i.createdAt)}</div>
                    </div>
                    {i.prospectId && <Button size="sm" variant="outline" onClick={() => openById(i.prospectId)} data-testid={`button-open-prospect-${i.id}`}>Open prospect</Button>}
                  </div>
                  {(i.problem || i.message) && <p className="mt-2 text-sm text-stone-700 whitespace-pre-wrap line-clamp-4">{i.problem}{i.problem && i.message ? "\n" : ""}{i.message}</p>}
                </li>
              ))}
            </ul>
          )
        ) : filtered.length === 0 ? (
          tab === "followups" ? <Empty title="Nothing due" body="No open prospects have a follow-up date today or earlier." />
            : prospects.length === 0 ? <Empty title="No prospects yet" body="Website inquiries become prospects automatically, or add one manually." action={<Button size="sm" onClick={() => openEdit(null)} className="bg-[#17324D] hover:bg-[#22476B]">Add prospect</Button>} />
            : <Empty title="No matches" body="Adjust the search or filters." />
        ) : (
          <ul className="divide-y divide-stone-200 rounded-md border border-stone-200 bg-white" data-testid="list-prospects">
            {filtered.map((p) => {
              const fs = followState(p);
              return (
                <li key={p.id}>
                  <button type="button" onClick={() => openEdit(p)} className="w-full text-left px-4 py-3 hover:bg-stone-50 focus:outline-none focus-visible:bg-[#EAF0F6] grid grid-cols-1 md:grid-cols-[1fr_140px_150px_24px] gap-2 items-center" data-testid={`row-prospect-${p.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#17324D] truncate">{p.name}</span>
                        {p.sourceInquiryId && <span className="text-[9px] uppercase tracking-wider font-bold bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">Website</span>}
                      </div>
                      <div className="text-xs text-[#5B6570] truncate">{[p.category, p.contactName, p.estOpportunity, p.assignedTo && `Tracking: ${p.assignedTo}`].filter(Boolean).join(" · ")}</div>
                    </div>
                    <span className={`justify-self-start text-xs font-medium px-2 py-0.5 rounded ${STATUS_STYLE[p.status] || STATUS_STYLE.new}`}>{statusLabel(p.status)}</span>
                    <span className={`text-xs flex items-center gap-1 ${fs === "overdue" ? "text-red-700 font-semibold" : fs === "due" ? "text-amber-700 font-semibold" : "text-[#5B6570]"}`}>
                      <CalendarBlank size={13} /> {p.nextFollowup ? `${fs === "overdue" ? "Overdue · " : fs === "due" ? "Today · " : ""}${fmtDate(p.nextFollowup)}` : "No follow-up"}
                    </span>
                    <PencilSimple size={15} className="hidden md:block text-stone-400" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o && !saving) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {editing && (
            <form onSubmit={save}>
              <DialogHeader>
                <DialogTitle className="text-[#17324D]">{editing.id ? "Edit prospect" : "New prospect"}</DialogTitle>
                <DialogDescription>Only company name is required. Assignment is for tracking and grants no access.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-4">
                <F id="name" label="Company *" full><Input id="name" value={editing.name} maxLength={MAX.name} onChange={set("name")} required /></F>
                <F id="category" label="Category"><select id="category" className={sel} value={editing.category} onChange={set("category")}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></F>
                <F id="status" label="Stage"><select id="status" className={sel} value={editing.status} onChange={set("status")}>{STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select></F>
                <F id="contactName" label="Contact name"><Input id="contactName" value={editing.contactName} maxLength={MAX.contactName} onChange={set("contactName")} /></F>
                <F id="contactEmail" label="Contact email"><Input id="contactEmail" type="email" value={editing.contactEmail} maxLength={MAX.contactEmail} onChange={set("contactEmail")} /></F>
                <F id="whyFit" label="Problem / why a fit" full><Textarea id="whyFit" rows={3} value={editing.whyFit} maxLength={MAX.whyFit} onChange={set("whyFit")} /></F>
                <F id="likelyNeed" label="Likely need"><select id="likelyNeed" className={sel} value={editing.likelyNeed} onChange={set("likelyNeed")}>
                  {editing.likelyNeed && !LIKELY_NEEDS.includes(editing.likelyNeed) && <option value={editing.likelyNeed} disabled>{editing.likelyNeed} (not accepted — choose another)</option>}
                  {LIKELY_NEEDS.map((o) => <option key={o} value={o}>{o || "Not set"}</option>)}
                </select></F>
                <F id="estOpportunity" label="Estimated opportunity"><select id="estOpportunity" className={sel} value={editing.estOpportunity} onChange={set("estOpportunity")}>{OPPS.map((o) => <option key={o} value={o}>{o || "Not set"}</option>)}</select></F>
                <F id="assignedTo" label="Assigned to (tracking only)"><Input id="assignedTo" value={editing.assignedTo} maxLength={MAX.assignedTo} onChange={set("assignedTo")} /></F>
                <F id="nextFollowup" label="Next follow-up"><Input id="nextFollowup" type="date" value={editing.nextFollowup} onChange={set("nextFollowup")} /></F>
                <F id="lastContact" label="Last contact"><Input id="lastContact" type="date" value={editing.lastContact} onChange={set("lastContact")} /></F>
                <F id="deadline" label="Deadline"><Input id="deadline" type="date" value={editing.deadline} onChange={set("deadline")} /></F>
                <F id="scope" label="Scope" full><Textarea id="scope" rows={2} value={editing.scope} maxLength={MAX.scope} onChange={set("scope")} /></F>
                <F id="notes" label="Notes" full><Textarea id="notes" rows={3} value={editing.notes} maxLength={MAX.notes} onChange={set("notes")} /></F>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-[#17324D] hover:bg-[#22476B]" data-testid="button-save-prospect">{saving ? "Saving…" : "Save"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function F({ id, label, full, children }) {
  return <div className={full ? "sm:col-span-2" : ""}><Label htmlFor={id} className="text-xs text-[#5B6570] mb-1 block">{label}</Label>{children}</div>;
}

function Empty({ title, body, action }) {
  return (
    <div className="rounded-md border border-dashed border-stone-300 bg-white p-10 text-center">
      <Tray size={28} className="mx-auto text-[#C69A4B] mb-2" />
      <div className="font-semibold text-[#17324D]">{title}</div>
      <p className="text-sm text-[#5B6570] mt-1 mb-3">{body}</p>
      {action}
    </div>
  );
}
